import { useState } from "react";

import { apiCall } from "api/axios";
import { encryptPayload } from "utils";

import { customErrorToast } from "ui/custom-toast";

export const validDocumentLabel = [
  "cac",
  "utility-bill",
  "driver's-license",
  "international-passport",
  "particulars-of-directors",
  "national-id",
  "voter's-card",
  "lease-agreement",
  "others",
  "bank-statement",
];

export type TDocumentName =
  | "cac"
  | "utility-bill"
  | "driver's-license"
  | "international-passport"
  | "particulars-of-directors"
  | "national-id"
  | "voter's-card"
  | "lease-agreement"
  | "others"
  | "bank-statement";

export interface IUploadFilePayload {
  file: File;
  documentName: TDocumentName;
  companyName: string;
}

export interface IMultiFileUploadResultItem {
  fileUrl: string;
  documentName: TDocumentName;
  companyName: string;
}

export interface IFileUploadResult {
  status: "success" | "failed";
  message: string;
  url: string;
  error?: boolean;
}

export interface IMultiUploadResponse {
  didAllFilesUploadSuccessfully: boolean;
  multiFileUploadResults: IMultiFileUploadResultItem[];
}

export const useUploadDocument = () => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (payload: IUploadFilePayload) => {
    const { file, documentName, companyName } = payload;
    setIsUploading(true);
    const filePayload = {
      companyName,
      documentName,
    };
    const encryptedFilePayload = encryptPayload(filePayload);
    const data = new FormData();
    data.append("file", file);
    data.append("iv", encryptedFilePayload?.iv);
    data.append("encryptedValue", encryptedFilePayload?.encryptedValue);
    const result = await apiCall({
      apiRoute: "/user-management/employer/upload-employer-document",
      module: "EARNIPAY_STG",
      config: {
        method: "POST",
        data,
      },
      useEncryption: false,
    });
    setIsUploading(false);
    return result as IFileUploadResult;
  };

  const uploadMultipleFiles = async (
    files: IUploadFilePayload[]
  ): Promise<IMultiUploadResponse> => {
    const multiFileUploadResults: IMultiFileUploadResultItem[] = [];
    setIsUploading(true);
    for (const fileItem of files) {
      const { file, documentName, companyName } = fileItem;
      const filePayload = {
        documentName,
        companyName,
      };
      const encryptedFilePayload = encryptPayload(filePayload);
      const data = new FormData();
      data.append("file", file);
      data.append("iv", encryptedFilePayload?.iv);
      data.append("encryptedValue", encryptedFilePayload?.encryptedValue);
      const response = await apiCall({
        apiRoute: "/user-management/employer/upload-employer-document",
        module: "EARNIPAY_STG",
        config: {
          method: "POST",
          data,
        },
        useEncryption: false,
      });
      const result = response as IFileUploadResult;
      if (result.error) {
        customErrorToast({
          message: `${result.message} for ${file.name}`,
        });
        setIsUploading(false);
        return {
          didAllFilesUploadSuccessfully: false,
          multiFileUploadResults: [],
        };
      }

      multiFileUploadResults.push({
        fileUrl: result.url,
        companyName,
        documentName,
      });
    }
    setIsUploading(false);
    return { didAllFilesUploadSuccessfully: true, multiFileUploadResults };
  };

  /* const addSingleBusinessDocument = (docs, name, newDoc) => {
    docs = docs.filter((doc) => doc.name !== name);
    docs.push(newDoc);
    return docs;
  }; */

  return {
    uploadFile,
    isUploading,
    uploadMultipleFiles,
    // addSingleBusinessDocument,
  };
};
